/**
 * MarkdownMessage — Renders chat message content as markdown with math support.
 * Supports GitHub-flavored markdown (tables, fenced code) and LaTeX via KaTeX.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

interface MarkdownMessageProps {
  content: string;
  inverted?: boolean; // true when rendered on dark background (user bubble)
}

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content, inverted = false }) => {
  const textColor = inverted ? '#fff' : 'rgba(255,255,255,0.88)';
  const linkColor = inverted ? '#9ec5fe' : '#40a9ff';
  const codeBg = inverted ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)';
  const codeBorder = inverted ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.12)';
  const tableBorder = inverted ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.15)';

  return (
    <div className="md-content" style={{ color: textColor, fontSize: 13, lineHeight: 1.6 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          p: ({ children }) => <p style={{ margin: '6px 0' }}>{children}</p>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: linkColor }}>
              {children}
            </a>
          ),
          code: ({ inline, className, children, ...props }: any) => {
            if (inline) {
              return (
                <code
                  style={{
                    background: codeBg,
                    border: `1px solid ${codeBorder}`,
                    borderRadius: 4,
                    padding: '1px 6px',
                    fontSize: 12,
                    fontFamily: 'Menlo, Consolas, monospace',
                  }}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <pre
                style={{
                  background: codeBg,
                  border: `1px solid ${codeBorder}`,
                  borderRadius: 6,
                  padding: '8px 10px',
                  margin: '6px 0',
                  overflowX: 'auto',
                  fontSize: 12,
                  fontFamily: 'Menlo, Consolas, monospace',
                }}
              >
                <code className={className}>{children}</code>
              </pre>
            );
          },
          table: ({ children }) => (
            <div style={{ overflowX: 'auto', margin: '8px 0' }}>
              <table
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  fontSize: 12,
                }}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th
              style={{
                border: `1px solid ${tableBorder}`,
                padding: '4px 8px',
                background: codeBg,
                textAlign: 'left',
                fontWeight: 600,
              }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td
              style={{
                border: `1px solid ${tableBorder}`,
                padding: '4px 8px',
              }}
            >
              {children}
            </td>
          ),
          ul: ({ children }) => (
            <ul style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ol>
          ),
          li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
          h1: ({ children }) => (
            <h1 style={{ fontSize: 18, margin: '10px 0 6px', fontWeight: 600 }}>{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 style={{ fontSize: 16, margin: '10px 0 6px', fontWeight: 600 }}>{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 style={{ fontSize: 14, margin: '8px 0 4px', fontWeight: 600 }}>{children}</h3>
          ),
          blockquote: ({ children }) => (
            <blockquote
              style={{
                borderLeft: `3px solid ${tableBorder}`,
                margin: '6px 0',
                paddingLeft: 10,
                color: 'rgba(255,255,255,0.65)',
              }}
            >
              {children}
            </blockquote>
          ),
          hr: () => (
            <hr
              style={{
                border: 'none',
                borderTop: `1px solid ${tableBorder}`,
                margin: '10px 0',
              }}
            />
          ),
          strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
